import { calcFixedPayment } from './payments.js';
import { roundCents } from './rounding.js';
import { addMonthsToYearMonth } from './timeline.js';
import type { Scenario } from './types.js';

const DAY_MS = 86_400_000;

function yearDays(year: number): number {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 366 : 365;
}

/** Parse a real ISO calendar date, rejecting rollover dates such as February 30. */
export function isCalendarDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const time = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(time) && new Date(time).toISOString().slice(0, 10) === value;
}

/** UTC arithmetic preserves the contractual day after short months and ignores local DST. */
export function calendarPaymentDate(startMonth: string, monthIndex: number, paymentDay: number): string {
  const [year, month] = addMonthsToYearMonth(startMonth, monthIndex).split('-').map(Number);
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(Math.min(paymentDay, lastDay)).padStart(2, '0')}`;
}

/**
 * Actual/Actual simple interest: exclude the previous date, include the due date.
 * Round each part with a different year-length denominator separately. Consecutive
 * non-leap years form one part. This rounding matches the supplied 360-row schedule.
 */
export function calcCalendarInterest(balance: number, apr: number, from: string, to: string): number {
  let cursor = Date.parse(`${from}T00:00:00Z`) + DAY_MS;
  const end = Date.parse(`${to}T00:00:00Z`);
  let denominator = 0;
  let groupedDays = 0;
  let interest = 0;
  while (cursor <= end) {
    const year = new Date(cursor).getUTCFullYear();
    const daysInYear = yearDays(year);
    const nextYear = Date.UTC(year + 1, 0, 1);
    const segmentEnd = Math.min(end + DAY_MS, nextYear);
    if (denominator && denominator !== daysInYear) {
      interest += roundCents(balance * apr / 100 * groupedDays / denominator);
      groupedDays = 0;
    }
    denominator = daysInYear;
    groupedDays += (segmentEnd - cursor) / DAY_MS;
    cursor = segmentEnd;
  }
  return interest + (groupedDays ? roundCents(balance * apr / 100 * groupedDays / denominator) : 0);
}

/** Interest for a 31-day month at 365: the lowest calendar annuity payment for `balance`. */
export function calendarInterestFloor(balance: number, apr: number): number {
  return roundCents(balance * apr / 100 * 31 / 365);
}

/**
 * A recalculated calendar annuity: never below the interest for a 31-day month at 365,
 * so a long month cannot outgrow the payment early in the term (APR / 12 is less).
 */
export function calendarAnnuity(balance: number, termMonths: number, apr: number): number {
  return Math.max(calcFixedPayment(balance, termMonths, apr), calendarInterestFloor(balance, apr));
}

/** The first payment also covers a first period longer than 31 days. */
export function initialCalendarPayment(s: Scenario, firstInterest: number, apr: number): number {
  return Math.max(calendarAnnuity(s.principal, s.termMonths, apr), firstInterest);
}

/**
 * Remaining term after a reduce_term overpayment: payments of the retained `payment` at the
 * current `apr` until the debt is gone, as a bank recalculates the term. A later rate change
 * then recalculates the payment over this shortened term, so the term cut is kept.
 */
export function calendarRemainingTerm(
  s: Scenario, balance: number, payment: number, apr: number, nextMonth: number, limit: number,
): number {
  const day = s.bankCalendar!.paymentDay;
  let previous = calendarPaymentDate(s.startDate, nextMonth - 1, day);
  for (let count = 1; count <= limit; count++) {
    const date = calendarPaymentDate(s.startDate, nextMonth + count - 1, day);
    const interest = calcCalendarInterest(balance, apr, previous, date);
    if (balance + interest <= payment) return count;
    balance -= payment - interest;
    previous = date;
  }
  return limit;
}
